import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";

// Define styles
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#333",
    padding: 30,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    borderBottom: "4px solid #154734",
    paddingBottom: 10,
  },
  logoSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoImage: {
    width: 40,
    height: 40,
    marginRight: 10,
    objectFit: "cover",
    borderRadius: "50%",
    overflow: "hidden",
  },
  vendorName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#154734",
  },
  invoiceTitle: {
    fontSize: 15,
    fontWeight: "bold",
    textTransform: "uppercase",
    textAlign: "right",
    color: "#154734",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#154734",
    marginBottom: 5,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#154734",
    color: "#fff",
    padding: 8,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #ccc",
    padding: 8,
  },
  tableCell: {
    flex: 1,
    textAlign: "center",
  },
  totalSection: {
    marginTop: 20,
    paddingTop: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    padding: 5,
    fontSize: 11,
  },
  totalLabel: {
    fontWeight: "bold",
    color: "#154734",
  },
  totalValue: {
    fontWeight: "bold",
    color: "#333",
  },
  grandTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 8,
    marginTop: 10,
    backgroundColor: "#154734",
    color: "#fff",
    fontSize: 12,
  },
});

// PDF Document Component
const OrderPDF = ({ order }) => {

  return (
    <Document>
      <Page style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoSection}>
            <Image src={order.vendorDetails.pfp} style={styles.logoImage} />
            <Text style={styles.vendorName}>{order.vendorDetails.name}</Text>
          </View>
          <Text style={styles.invoiceTitle}>{order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)} Order</Text>
        </View>

        {/* Order Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Order Details:</Text>
          <Text>Order ID: {order.orderID}</Text>
          <Text>Payment Type: {order.payment.method}</Text>
          <Text>Created: {new Date(order.createdAt).toLocaleDateString()}</Text>
        </View>

        {/* Customer Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Customer Details:</Text>
          <Text>Name: {order.customer.name}</Text>
          <Text>Phone: {order.customer.phone}</Text>
          <Text>Email: {order.customer.email}</Text>
        </View>

        {/* Product Orders */}
        {order.orderType === "product" && (
          <View style={styles.section}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { flex: 0.2 }]}>Quantity</Text>
              <Text style={[styles.tableCell, { flex: 0.4 }]}>Item Description</Text>
              <Text style={[styles.tableCell, { flex: 0.2 }]}>Price Per</Text>
              <Text style={[styles.tableCell, { flex: 0.2 }]}>Subtotal</Text>
              <Text style={[styles.tableCell, { flex: 0.2 }]}>Tax</Text>
              <Text style={[styles.tableCell, { flex: 0.2 }]}>Total</Text>
            </View>
            {order.items.map((item, index) => {
              const quantity = item.quantity;
              const name = item.name;
              const pricePerItem = item.price;
              const subtotal = item.subtotal;
              const taxesApplied = item.taxesApplied;
              const total = item.total;
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 0.2 }]}>{quantity}</Text>
                  <Text style={[styles.tableCell, { flex: 0.4 }]}>
                    {name.length > 16 ? `${name.substring(0, 24)}...` : name}
                  </Text>
                  <Text style={[styles.tableCell, { flex: 0.2 }]}>${pricePerItem.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, { flex: 0.2 }]}>${subtotal.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, { flex: 0.2 }]}>${taxesApplied.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, { flex: 0.2 }]}>${total.toFixed(2)}</Text>
                </View>
              );
            })}

            {/* Totals Section */}
            <View style={styles.totalSection}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal:</Text>
                <Text style={styles.totalValue}>${order.orderDetails.subtotal.toFixed(2)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Tax (8.25%):</Text>
                <Text style={styles.totalValue}>{order.orderDetails.taxes === 0.00 ? 'No Tax' : `$${order.orderDetails.taxes.toFixed(2)}`}</Text>
              </View>
              <View style={styles.grandTotal}>
                <Text>Grand Total:</Text>
                <Text>${order.orderDetails.total.toFixed(2)}</Text>
              </View>
            </View>
          </View>
        )}

        {order.orderType === "service" && (
          <View
            style={{
              marginBottom: 20,
              padding: 15,
              borderRadius: 5,
              border: "1px solid #154734",
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: "bold",
                color: "#154734",
                marginBottom: 10,
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              Service Details
            </Text>

            <View style={{ marginBottom: 10, flexDirection: "row" }}>
              <Text style={{ flex: 1, fontWeight: "bold", color: "#154734" }}>
                Service Name:
              </Text>
              <Text style={{ flex: 2, textAlign: "right", color: "#333" }}>
                {order.service.name}
              </Text>
            </View>

            <View style={{ marginBottom: 10, flexDirection: "row" }}>
              <Text style={{ flex: 1, fontWeight: "bold", color: "#154734" }}>Cost:</Text>
              <Text style={{ flex: 2, textAlign: "right", color: "#333" }}>
                ${order.service.cost.toFixed(2)} ({order.service.costMode})
              </Text>
            </View>

            <View style={{ flexDirection: "row" }}>
              <Text style={{ flex: 1, fontWeight: "bold", color: "#154734" }}>
                Requested For:
              </Text>
              <Text style={{ flex: 2, textAlign: "right", color: "#333" }}>
                {order.requestedTime} on {order.requestedDate}
              </Text>
            </View>
          </View>
        )}

      </Page>
    </Document>
  );
};

export default OrderPDF;
